/**
 * MessagesByAgentTable Component
 * Per-agent message-volume breakdown — which AI agent handled how much.
 *
 * Tenants aren't capped at one AI agent (agent count is plan-limited, not
 * fixed at one), and a single long-lived conversation can be handled by more
 * than one agent over its life — so this reads real per-response attribution
 * from WhatsAppMessage.metadata.selectedAgent (server-side), not the
 * lossy, last-write-wins Conversation.metadata.selectedAgent snapshot.
 *
 * Server order preserved (sorted desc by messages) — this component renders
 * what it receives and never re-sorts, same contract as ChatsByCountryMap.
 */

import React from 'react';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import { AgentMessageBreakdown } from '@/hooks/useAnalytics';

interface MessagesByAgentTableProps {
  agents: AgentMessageBreakdown[];
  unattributedMessages: number;
  /** Agent _id → display name, resolved client-side from the tenant's agent
   *  list (the same list the AI Agent filter dropdown uses) — an id with no
   *  match (a deleted agent) falls back to a labeled placeholder instead of
   *  vanishing from the breakdown. */
  agentNameById: Record<string, string>;
  loading: boolean;
}

const MessagesByAgentTable: React.FC<MessagesByAgentTableProps> = ({
  agents,
  unattributedMessages,
  agentNameById,
  loading,
}) => {
  const totalMessages = agents.reduce((s, a) => s + a.messages, 0) + unattributedMessages;
  const pct = (n: number) => (totalMessages > 0 ? Math.round((n / totalMessages) * 1000) / 10 : 0);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Messages by AI Agent
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography color="text.secondary">Loading agent breakdown...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (agents.length === 0 && unattributedMessages === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Messages by AI Agent
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography color="text.secondary">No agent activity in this range</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const rows = [
    ...agents.map((a) => ({
      key: a.agentId,
      label: agentNameById[a.agentId] || `Unknown agent (${a.agentId.slice(-6)})`,
      messages: a.messages,
      conversations: a.conversations,
      muted: false,
    })),
    ...(unattributedMessages > 0
      ? [
          {
            key: '__unattributed',
            label: 'Unattributed (customer replies, human/manual, campaign sends)',
            messages: unattributedMessages,
            conversations: undefined as number | undefined,
            muted: true,
          },
        ]
      : []),
  ];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" mb={2}>
          Messages by AI Agent
        </Typography>
        <Box>
          {rows.map((row) => (
            <Box key={row.key} mb={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography
                  variant="body2"
                  fontWeight={row.muted ? 'normal' : 'medium'}
                  color={row.muted ? 'text.secondary' : 'text.primary'}
                >
                  {row.label}
                  {row.conversations !== undefined && (
                    <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                      ({row.conversations} conversation{row.conversations === 1 ? '' : 's'})
                    </Typography>
                  )}
                </Typography>
                <Chip
                  label={`${row.messages} (${pct(row.messages)}%)`}
                  size="small"
                  color={row.muted ? 'default' : 'primary'}
                />
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: 8,
                  backgroundColor: 'grey.200',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${pct(row.messages)}%`,
                    height: '100%',
                    backgroundColor: row.muted ? 'grey.400' : 'primary.main',
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MessagesByAgentTable;
